import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/Dialog';
import { Mitarbeiter, Zuweisung, Projekt, Abwesenheit } from '../types';
import { Mail, Phone, Calendar, Briefcase, User, Clock, MapPin } from 'lucide-react';
import { cn } from '../lib/utils';
interface EmployeeDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: Mitarbeiter | null;
  zuweisungen: Zuweisung[];
  projekte: Projekt[];
  abwesenheiten: Abwesenheit[];
}
export function EmployeeDetailDialog({
  open,
  onOpenChange,
  employee,
  zuweisungen,
  projekte,
  abwesenheiten
}: EmployeeDetailDialogProps) {
  if (!employee) return null;
  // Filter assignments for this employee
  const employeeAssignments = zuweisungen.filter(z => z.mitarbeiterId === employee.id);
  // Filter absences for this employee
  const employeeAbsences = abwesenheiten.filter(a => a.mitarbeiterId === employee.id);
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-78">
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden gap-0 border-none shadow-2xl" data-id="element-79">
        {/* Header with Avatar */}
        <div className="bg-muted/30 p-6 border-b flex items-start gap-5" data-id="element-80">
          <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border-2 border-background shadow-md flex items-center justify-center text-primary text-2xl font-bold" data-id="element-81">
            {employee.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div className="flex-1 pt-1" data-id="element-82">
            <DialogHeader className="text-left p-0 space-y-1" data-id="element-83">
              <DialogTitle className="text-2xl font-bold tracking-tight" data-id="element-84">
                {employee.name}
              </DialogTitle>
              <div className="flex items-center gap-2 text-muted-foreground font-medium" data-id="element-85">
                <Briefcase className="h-4 w-4" data-id="element-86" />
                {employee.abteilung}
              </div>
            </DialogHeader>

            <div className="flex flex-wrap gap-2 mt-4" data-id="element-87">
              {employee.skills.map(skill => <span key={skill} className="px-2.5 py-0.5 rounded-full bg-background border text-xs font-medium text-muted-foreground shadow-sm" data-id="element-88">
                  {skill}
                </span>)}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar" data-id="element-89">
          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4" data-id="element-90">
            <a href={`mailto:${employee.email}`} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/5 hover:bg-muted/20 transition-colors group" data-id="element-91">
              <div className="h-8 w-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-92">
                <Mail className="h-4 w-4" data-id="element-93" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-94">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-95">
                  Email
                </div>
                <div className="text-sm font-medium truncate" data-id="element-96">
                  {employee.email}
                </div>
              </div>
            </a>

            <a href={`tel:${employee.telefon}`} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/5 hover:bg-muted/20 transition-colors group" data-id="element-97">
              <div className="h-8 w-8 rounded-full bg-green-50 text-green-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-98">
                <Phone className="h-4 w-4" data-id="element-99" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-100">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-101">
                  Telefon
                </div>
                <div className="text-sm font-medium truncate" data-id="element-102">
                  {employee.telefon}
                </div>
              </div>
            </a>
          </div>

          {/* Availability */}
          <div className="space-y-3" data-id="element-103">
            <div className="flex items-center justify-between" data-id="element-104">
              <h3 className="text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2" data-id="element-105">
                <Clock className="h-4 w-4" data-id="element-106" />
                Verfügbarkeit
              </h3>
              <span className={cn('text-sm font-bold px-2 py-0.5 rounded', employee.verfuegbarkeit === 100 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700')} data-id="element-107">
                {employee.verfuegbarkeit}%
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-108">
              <div className={cn('h-full transition-all duration-500', employee.verfuegbarkeit === 100 ? 'bg-green-500' : 'bg-orange-500')} style={{
              width: `${employee.verfuegbarkeit}%`
            }} data-id="element-109" />
            </div>
          </div>

          <div className="h-px bg-border" data-id="element-110" />

          {/* Assignments */}
          <div className="space-y-4" data-id="element-111">
            <h3 className="text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2" data-id="element-112">
              <MapPin className="h-4 w-4" data-id="element-113" />
              Aktuelle Projekte
            </h3>

            {employeeAssignments.length === 0 ? <div className="text-sm text-muted-foreground italic py-2" data-id="element-114">
                Keine aktiven Zuweisungen.
              </div> : <div className="space-y-3" data-id="element-115">
                {employeeAssignments.map(assignment => {
              const project = projekte.find(p => p.id === assignment.projektId);
              if (!project) return null;
              return <div key={assignment.id} className="flex items-center gap-4 p-3 rounded-lg border bg-card shadow-sm" data-id="element-116">
                      <div className="h-10 w-10 rounded-md shadow-sm flex-shrink-0" style={{
                  backgroundColor: project.farbe
                }} data-id="element-117" />
                      <div className="flex-1 min-w-0" data-id="element-118">
                        <div className="font-bold text-sm truncate" data-id="element-119">
                          {project.name}
                        </div>
                        <div className="text-xs text-muted-foreground" data-id="element-120">
                          {project.projektnummer} • {assignment.phase}
                        </div>
                      </div>
                      <div className="text-right" data-id="element-121">
                        <div className="font-bold text-sm" data-id="element-122">
                          {assignment.auslastungProzent}%
                        </div>
                        <div className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded inline-block mt-0.5" data-id="element-123">
                          KW {assignment.startKw}-{assignment.endKw}
                        </div>
                      </div>
                    </div>;
            })}
              </div>}
          </div>

          {/* Absences */}
          {employeeAbsences.length > 0 && <div className="space-y-4" data-id="element-124">
              <h3 className="text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2" data-id="element-125">
                <Calendar className="h-4 w-4" data-id="element-126" />
                Geplante Abwesenheiten
              </h3>
              <div className="grid grid-cols-2 gap-3" data-id="element-127">
                {employeeAbsences.map(absence => <div key={absence.id} className="p-3 rounded-lg border bg-muted/10 flex items-center justify-between" data-id="element-128">
                    <div data-id="element-129">
                      <div className="font-medium text-sm" data-id="element-130">{absence.typ}</div>
                      <div className="text-xs text-muted-foreground" data-id="element-131">
                        KW {absence.startKw} - {absence.endKw}
                      </div>
                    </div>
                    <div className="h-2 w-2 rounded-full bg-muted-foreground/30" data-id="element-132" />
                  </div>)}
              </div>
            </div>}
        </div>
      </DialogContent>
    </Dialog>;
}